import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import useUserRoles from '../api/hooks/roles';
import { normalizePermissionTree } from './normalize';
import LoadingScreen from '../components/loading-screen';
import { FlatPermission } from '../interface/permissions';
import PermissionsErrorScreen from './permissionErrorScreen';

interface PermissionContextValue {
  permissionMap: Map<string, FlatPermission>;
  user: { userId: string; userName: string; roleName: string } | null;
  refetchRoles: () => void;
  isReady: boolean
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { rolesData, isRolesLoading, isRolesError, refetchRoles } = useUserRoles();
  const [hasRetried, setHasRetried] = useState(false);

  const permissionMap = useMemo(
    () =>
      rolesData?.permissions
        ? normalizePermissionTree(rolesData.permissions)
        : new Map<string, FlatPermission>(),
    [rolesData]
  );

  const handleRetry = () => {
    setHasRetried(true);
    refetchRoles();
  };

  if (isRolesLoading) {
    return <LoadingScreen />;
  }

  if (isRolesError) {
    return (
      <PermissionsErrorScreen
        isRetryFailed={hasRetried}
        onRetry={handleRetry}
      />
    );
  }

  const value: PermissionContextValue = {
    permissionMap,
    user: rolesData
      ? { userId: rolesData.userId, userName: rolesData.userName, roleName: rolesData.roleName }
      : null,
    refetchRoles,
    isReady: !isRolesLoading
  };

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissionContext() {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error('usePermissionContext must be used within PermissionProvider');
  return ctx;
}
